// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { BreakPriceDto, QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    quoteLine: QuoteLineModel;
    extendedStyles?: RfqQuoteDetailsQuotedPricingStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface RfqQuoteDetailsQuotedPricingStyles {
    table?: InjectableCss;
    row?: InjectableCss;
    quantityCell?: InjectableCss;
    quantityText?: TypographyPresentationProps;
    priceCell?: InjectableCss;
    priceWithoutVatCell?: InjectableCss;
    priceText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
}

export const rfqQuoteDetailsQuotedPricingStyles: RfqQuoteDetailsQuotedPricingStyles = {
    table: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
        `,
    },
    row: {
        css: css`
            width: 100%;
            display: flex;
            @media (max-width: 600px) {
                margin-bottom: 10px;
            }
        `,
    },
    quantityCell: {
        css: css`
            width: 50%;
            text-align: center;
            @media (max-width: 600px) {
                text-align: left !important;
            }
        `,
    },
    priceCell: {
        css: css`
            width: 50%;
            text-align: right;
            @media (max-width: 600px) {
                &,
                span,
                legend {
                    text-align: right !important;
                }
            }
        `,
    },
    priceText: {
        size: "18px",
        lineHeight: "22px",
        css: css`
            font-weight: bold;
            legend {
                font-size: 12px;
                line-height: 14px;
                font-weight: 400;
            }
        `,
    },
    quantityText: {
        size: "15px",
        lineHeight: "18px",
        css: css`
            font-weight: 500;
            legend {
                font-size: 12px;
                line-height: 14px;
                font-weight: 400;
            }
        `,
    },
};

const baseStyles = rfqQuoteDetailsQuotedPricingStyles;

const RfqQuoteDetailsQuotedPricing = ({ quote, quoteLine, enableVat, vatPriceDisplay, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeToNew(baseStyles, extendedStyles));

    const getBreakQtyText = (
        quoteLine: QuoteLineModel,
        breakPrices: BreakPriceDto[],
        breakPrice: BreakPriceDto,
        index: number,
    ) => {
        if (index !== breakPrices.length - 1) {
            return `${breakPrice.breakQty} - ${breakPrices[index + 1].breakQty - 1}`;
        }

        if (quoteLine.maxQty > 0) {
            return `${breakPrice.breakQty} - ${quoteLine.maxQty}`;
        }

        return `${breakPrice.breakQty}+`;
    };

    const renderRow = (index: number, qty: string, price: string, priceWithVat: string, realPrice?: number) => {
        return (
            <StyledWrapper key={qty} {...styles.row} data-test-selector={`rfqConfirmation_quotedPricing_${index}`}>
                <StyledWrapper {...styles.quantityCell} data-test-selector={`rfqQuoteDetailsView_quantity_${index}`}>
                    <VisuallyHidden>{translate("QTY")}</VisuallyHidden>
                    <Typography
                        {...styles.quantityText}
                        data-test-selector={`rfqQuoteDetailsView_quantityValue_${index}`}
                    >
                        <legend>{translate("Quoted Qty")}</legend>
                        {quoteLine.properties.isFixedQty === "true" ? translate("Fixed ") : ""}
                        {qty}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.priceCell} data-test-selector={`rfqQuoteDetailsView_price_${index}`}>
                    <VisuallyHidden>{translate("Price")}</VisuallyHidden>
                    <Typography {...styles.priceText} data-test-selector={`rfqQuoteDetailsView_priceValue_${index}`}>
                        <legend>{translate("Quoted Price")}</legend>
                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                            ? priceWithVat
                            : quoteLine.properties["pricingFailed"]?.toLowerCase() === "true" && !realPrice
                            ? "N/A"
                            : price}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    const renderRowWithQuote = (qty: string) => {
        return (
            <StyledWrapper key={qty} {...styles.row}>
                <StyledWrapper {...styles.quantityCell}>
                    <VisuallyHidden>{translate("QTY")}</VisuallyHidden>
                    <Typography {...styles.quantityText}>
                        <legend>{translate("Quoted Qty")}</legend>
                        {quoteLine.properties.isFixedQty === "true" ? translate("Fixed ") : ""}
                        {qty}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.priceCell}>
                    <VisuallyHidden>{translate("Price")}</VisuallyHidden>
                    <Typography {...styles.priceText}>
                        <legend>{translate("Quoted Price")}</legend>
                        {translate("Requires Quote")}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    const renderRows = () => {
        if (quote.isJobQuote && quoteLine.pricing) {
            return renderRow(
                0,
                quoteLine.qtyOrdered!.toString(),
                quoteLine.pricing!.unitNetPriceDisplay,
                quoteLine.pricing!.unitRegularPriceWithVatDisplay,
            );
        }

        if (!quoteLine.pricing && quoteLine.quoteRequired) {
            return renderRowWithQuote("+1");
        }

        if (!quoteLine.pricing?.unitRegularBreakPrices || quoteLine.pricing!.unitRegularBreakPrices.length === 0) {
            return renderRow(
                0,
                "1+",
                quoteLine.pricing!.unitNetPriceDisplay,
                quoteLine.pricing!.unitRegularPriceWithVatDisplay,
            );
        }

        const breakPrices = quoteLine.pricing.unitRegularBreakPrices;

        return (
            <>
                {breakPrices.map((breakPrice, index) => {
                    const breakQtyText =
                        quoteLine.properties.isFixedQty === "true"
                            ? breakPrice.breakQty.toString()
                            : getBreakQtyText(quoteLine, breakPrices, breakPrice, index);
                    return renderRow(
                        index,
                        breakQtyText,
                        breakPrice.breakPriceDisplay,
                        breakPrice.breakPriceWithVatDisplay,
                        breakPrice.breakPrice,
                    );
                })}
            </>
        );
    };

    return <StyledWrapper {...styles.table}>{renderRows()}</StyledWrapper>;
};

export default connect(mapStateToProps)(RfqQuoteDetailsQuotedPricing);
